import type { ReactNode } from 'react'

import { H3 } from '@elements/headings'
import { Skeleton } from '@elements/skeleton'

import { InventoryItem, Venue } from 'types/model'
import { InventoryCard } from './inventory-card'

export interface InventoryListingProps {
  title?: string
  inventory?: InventoryItem[]
  venue?: Venue
  onInventoryAdd?: (inv: InventoryItem) => void | Promise<void>
  loading?: boolean
  children?: ReactNode
}

export function InventoryListing({
  title,
  loading = false,
  onInventoryAdd,
  inventory = [],
  venue,
  children,
}: InventoryListingProps) {
  const noInventory = !inventory.length && !loading

  if ((noInventory && !children) || !venue) return null

  // SWR often sets `loading` true on the client while revalidating even when `inventory`
  // came from SSR/fallback — show skeleton only when we truly have nothing to paint yet.
  const showSkeleton = loading && inventory.length === 0

  return (
    <div className='flex flex-col items-start justify-center w-full px-3 space-y-3 transition-all h-fit'>
      {!showSkeleton && title && <H3 className='heading-lg'>{title}</H3>}
      {!showSkeleton &&
        inventory?.map((inv) => (
          <InventoryCard
            key={inv.id}
            venue={venue}
            inventory={inv}
            onAdd={onInventoryAdd}
          />
        ))}
      {showSkeleton && (
        <>
          <Skeleton className='w-24 h-6' />
          <InventoryCard.Skeleton />
          <InventoryCard.Skeleton />
          <InventoryCard.Skeleton />
        </>
      )}
      {children}
    </div>
  )
}
